import { doc, onSnapshot, setDoc, collection, getDocs, addDoc, deleteDoc, updateDoc, query, where, orderBy, arrayUnion } from 'firebase/firestore'
import { useEffect, useState } from 'react'
import { db, storage } from '../../firebase'
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage'
import { compressImageForUpload } from '../../utils/compressImage'
import { Box, Button, Snackbar, Alert, Stack, TextField, Typography, Card, CardContent, Divider, Switch, FormControlLabel, Grid, Tabs, Tab, IconButton, Paper, Accordion, AccordionSummary, AccordionDetails, Radio, RadioGroup, FormControl, FormLabel, MenuItem, Select, InputLabel } from '@mui/material'
import { ArrowUpward, ArrowDownward, Delete, CloudUpload, ExpandMore } from '@mui/icons-material'
import CategoryFiltersEditor, {
  type CategoryFiltersConfig,
  emptyCategoryFilters,
  parseCategoryFilters,
  serializeCategoryFilters,
  filter3OptionsToSubSections,
} from './CategoryFiltersEditor'

/** قطع غيار السيارات: حالة جديد/إستيراد بدل جديد/مستعمل */
function categoryConditionPreset(cat: { id: string; name: string }): 'new_used' | 'new_import' {
  const id = cat.id.trim()
  const name = cat.name.trim()
  if (id === 'قطع-غيار-السيارات') return 'new_import'
  if (name.includes('قطع غيار') && name.includes('زيوت')) return 'new_import'
  return 'new_used'
}

type BuyerRequestMode = 'search_only' | 'manual_only' | 'both'

type CategorySubSection = {
  id: string
  name: string
  nameEn?: string
  order: number
  isActive: boolean
}

type CategoryItem = {
  name: string
  nameEn?: string
  id: string
  order: number
  isActive: boolean
  imageUrl?: string
  googleCseEngineId?: string
  buyerRequestMode: BuyerRequestMode
  subSections: CategorySubSection[]
  categoryFilters: CategoryFiltersConfig
}

function parseCategorySubSections(raw: unknown): CategorySubSection[] {
  if (!Array.isArray(raw)) return []
  const list: CategorySubSection[] = []
  raw.forEach((item, index) => {
    if (!item || typeof item !== 'object') return
    const m = item as Record<string, unknown>
    const name = typeof m.name === 'string' ? m.name.trim() : ''
    if (!name) return
    list.push({
      id: typeof m.id === 'string' && m.id.trim() ? m.id.trim() : `sub_${index}_${Date.now()}`,
      name,
      nameEn: typeof m.nameEn === 'string' ? m.nameEn.trim() : '',
      order: typeof m.order === 'number' ? m.order : index,
      isActive: m.isActive !== false,
    })
  })
  list.sort((a, b) => (a.order !== b.order ? a.order - b.order : a.name.localeCompare(b.name)))
  return list
}

/** إعدادات الفئات في Firestore يجب أن تُربط بـ categoryId لا بالاسم المعروض — مع دعم مفاتيح قديمة (اسم عربي/إنجليزي). */
function pickCategoryNumericSetting(
  saved: Record<string, number>,
  cat: CategoryItem,
  defaultVal: number
): number {
  const v =
    saved[cat.id] ??
    saved[cat.name] ??
    (cat.nameEn && saved[cat.nameEn] !== undefined ? saved[cat.nameEn] : undefined)
  return typeof v === 'number' && !Number.isNaN(v) ? v : defaultVal
}

function pickCommissionPercentFromDoc(data: Record<string, unknown>, cat: CategoryItem): number {
  const raw =
    data[cat.id] ??
    data[cat.name] ??
    (cat.nameEn && data[cat.nameEn] !== undefined ? data[cat.nameEn] : undefined)
  return typeof raw === 'number' ? raw * 100 : 0
}

function parseBuyerRequestMode(raw: unknown): BuyerRequestMode {
  const v = String(raw ?? '').trim()
  if (v === 'manual_only' || v === 'both') return v
  return 'search_only'
}

export default function SettingsPage() {
  const [minRangeKm, setMinRangeKm] = useState<number>(1)
  const [maxRangeKm, setMaxRangeKm] = useState<number>(100)
  const [allowedSearchMode, setAllowedSearchMode] = useState<'radius' | 'smart'>('radius')
  const [saving, setSaving] = useState(false)
  const [snack, setSnack] = useState<{open: boolean; msg: string; sev: 'success'|'error'}>({open: false, msg: '', sev: 'success'})
  
  // إعدادات مدة صلاحية العروض
  const [categoryExpiry, setCategoryExpiry] = useState<Record<string, number>>({})
  const [availableCategories, setAvailableCategories] = useState<Array<CategoryItem>>([])
  const [newCategoryName, setNewCategoryName] = useState<string>('')
  const [newCategoryNameEn, setNewCategoryNameEn] = useState<string>('')
  const [savingCategory, setSavingCategory] = useState(false)
  const [savingCategoryOrder, setSavingCategoryOrder] = useState(false)
  const [currentTab, setCurrentTab] = useState(0)
  const [savingExpiry, setSavingExpiry] = useState(false)
  const [categoryDrafts, setCategoryDrafts] = useState<Record<string, string>>({})
  const [categoryEnDrafts, setCategoryEnDrafts] = useState<Record<string, string>>({})
  const [categoryCseDrafts, setCategoryCseDrafts] = useState<Record<string, string>>({})
  const [savingCategoryFiltersId, setSavingCategoryFiltersId] = useState<string | null>(null)
  const [uploadingCategoryIconId, setUploadingCategoryIconId] = useState<string | null>(null)

  const [orderConditionsSettings, setOrderConditionsSettings] = useState({
    enabled: true,
    showElectronicPayment: true,
    showInstallment: true,
    showDelivery: true,
    showStorePickup: true,
  })
  const [savingOrderConditions, setSavingOrderConditions] = useState(false)

  // إعدادات نسبة العمولة
  const [commissionRates, setCommissionRates] = useState<Record<string, number>>({})
  const [savingCommission, setSavingCommission] = useState(false)

  // إعدادات طرق الدفع
  const [paymentMethods, setPaymentMethods] = useState<Record<string, boolean>>({
    creditCard: true,
    electronicWallet: true,
    appWallet: true,
    bankTransfer: true,
    testPayment: true,
    cashOnDelivery: false, // معطل افتراضياً
  })
  const [savingPaymentMethods, setSavingPaymentMethods] = useState(false)

  // إعدادات مدة حجز المنتج
  const [reservationDuration, setReservationDuration] = useState<Record<string, number>>({})
  const [savingReservationDuration, setSavingReservationDuration] = useState(false)

  // إعدادات مدة صلاحية الطلبات
  const [requestExpiry, setRequestExpiry] = useState<Record<string, number>>({})
  const [savingRequestExpiry, setSavingRequestExpiry] = useState(false)

  // إعدادات عدد الطلبات المسموح في نفس الوقت
  const [maxConcurrentRequests, setMaxConcurrentRequests] = useState<number>(1)
  const [savingMaxRequests, setSavingMaxRequests] = useState(false)

  // إعدادات قيود المشترين
  const [buyerRestrictions, setBuyerRestrictions] = useState({
    maxCancellations7Days: 3,
    maxCancellations30Days: 10,
    cooldownHoursExcellentAcceptance: 1,  // نسبة قبول >= 50%
    cooldownHoursHighAcceptance: 2,        // نسبة قبول 30-49%
    cooldownHoursMediumAcceptance: 6,      // نسبة قبول 20-29%
    cooldownHoursLowAcceptance: 24,        // نسبة قبول < 20%
    maxRequestsNewUserDay1: 2,
    maxRequestsNewUserWeek1: 5,
  })
  const [savingBuyerRestrictions, setSavingBuyerRestrictions] = useState(false)

  // إعدادات صور الـ Slider
  const [sliderImages, setSliderImages] = useState<Array<{id: string, imageUrl: string, order: number, isActive: boolean}>>([])
  const [uploadingSliderImage, setUploadingSliderImage] = useState(false)
  const [updatingSliderImage, setUpdatingSliderImage] = useState<string | null>(null)

  // إعدادات البحث الذكي (Smart Search) — محلي فقط
  const [smartSearch, setSmartSearch] = useState({
    wave_interval_minutes: 5,
    initial_merchants_count: 5,
    wave_expansion_count: 5,
    max_waves: 5,
    max_offers_limit: 15,
    min_offers_to_stop_expansion: 5,
    enforce_governorate_boundary: false,
  })
  const [savingSmartSearch, setSavingSmartSearch] = useState(false)

  // تذكير التجار بالطلبات الجديدة
  const [requestReminders, setRequestReminders] = useState({
    enabled: true,
    first_delay_seconds: 60,
    max_reminders_per_seller: 1,
    second_delay_seconds: 180,
    reminder_title_ar: 'تذكير: طلب جديد بانتظارك!',
    reminder_body_ar: 'لم تفتح بعد طلب {productName}',
  })
  const [savingRequestReminders, setSavingRequestReminders] = useState(false)

  // المظهر الافتراضي للمستخدمين الجدد
  const [defaultThemeMode, setDefaultThemeMode] = useState<'light' | 'dark' | 'system'>('light')
  const [savingThemeDefault, setSavingThemeDefault] = useState(false)

  // مساعد: توحيد فئات التاجر على categoryId (مع دعم بيانات قديمة بالأسماء عربي/إنجليزي).
  const renameCategoryForSellers = async (
    categoryId: string,
    oldName: string,
    newName: string,
    oldNameEn: string,
    newNameEn: string
  ) => {
    try {
      console.log(`🔄 بدء توحيد فئات التجار إلى categoryId="${categoryId}"...`)
      const usersSnap = await getDocs(collection(db, 'users'))
      let updatedCount = 0

      const aliasToId = new Map<string, string>()
      availableCategories.forEach((cat) => {
        const aliases = [cat.id, cat.name, cat.nameEn || '']
        aliases.forEach((a) => {
          const key = a.trim().toLowerCase()
          if (key) aliasToId.set(key, cat.id)
        })
      })

      ;[oldName, newName, oldNameEn, newNameEn, categoryId].forEach((a) => {
        const key = a.trim().toLowerCase()
        if (key) aliasToId.set(key, categoryId)
      })

      const normalizeToIds = (arr: string[]) => {
        const normalized = arr
          .map((v) => {
            const key = String(v || '').trim().toLowerCase()
            if (!key) return ''
            return aliasToId.get(key) || String(v).trim()
          })
          .filter(Boolean)
        return Array.from(new Set(normalized))
      }

      for (const userDoc of usersSnap.docs) {
        const data = userDoc.data() as any
        if (data.type !== 'seller') continue

        let changed = false
        const updatePayload: any = {}

        if (Array.isArray(data.allowedCategories)) {
          const nextAllowed = normalizeToIds(data.allowedCategories as string[])
          // مقارنة بسيطة: لو السلاسل المختلفة
          if (JSON.stringify(nextAllowed) !== JSON.stringify(data.allowedCategories)) {
            updatePayload.allowedCategories = nextAllowed
            changed = true
          }
        }

        if (Array.isArray(data.categories)) {
          const nextCats = normalizeToIds(data.categories as string[])
          if (JSON.stringify(nextCats) !== JSON.stringify(data.categories)) {
            updatePayload.categories = nextCats
            changed = true
          }
        }

        if (changed) {
          updatePayload.updatedAt = new Date().toISOString()
          await updateDoc(userDoc.ref, updatePayload)
          updatedCount++
        }
      }

      if (updatedCount > 0) {
        console.log(
          `✅ تم توحيد فئات ${updatedCount} تاجر إلى categoryId وربطها بشكل متوافق مع اللغتين.`
        )
      } else {
        console.log(
          `ℹ️ لا توجد تحديثات لازمة في فئات التجار.`
        )
      }
    } catch (e) {
      console.error(
        `❌ خطأ في توحيد فئات التجار إلى categoryId داخل users:`,
        e
      )
    }
  }

  useEffect(() => {
    const ref = doc(db, 'settings', 'global')
    const unsub = onSnapshot(ref, snap => {
      const data = snap.data() || {}
      setMinRangeKm(data.minRangeKm ?? 1)
      setMaxRangeKm(data.maxRangeKm ?? 100)
      setAllowedSearchMode((data.allowedSearchMode === 'smart' || data.allowedSearchMode === 'radius') ? data.allowedSearchMode : 'radius')
      setMaxConcurrentRequests(data.maxConcurrentRequests ?? 1)
      
      // تحميل إعدادات قيود المشترين
      if (data.buyerRestrictions) {
        setBuyerRestrictions({
          maxCancellations7Days: data.buyerRestrictions.maxCancellations7Days ?? 3,
          maxCancellations30Days: data.buyerRestrictions.maxCancellations30Days ?? 10,
          cooldownHoursExcellentAcceptance: data.buyerRestrictions.cooldownHoursExcellentAcceptance ?? 1,
          cooldownHoursHighAcceptance: data.buyerRestrictions.cooldownHoursHighAcceptance ?? 2,
          cooldownHoursMediumAcceptance: data.buyerRestrictions.cooldownHoursMediumAcceptance ?? 6,
          cooldownHoursLowAcceptance: data.buyerRestrictions.cooldownHoursLowAcceptance ?? 24,
          maxRequestsNewUserDay1: data.buyerRestrictions.maxRequestsNewUserDay1 ?? 2,
          maxRequestsNewUserWeek1: data.buyerRestrictions.maxRequestsNewUserWeek1 ?? 5,
        })
      }
    })
    return () => unsub()
  }, [])

  // تحميل الفئات المتاحة
  useEffect(() => {
    loadAvailableCategories()
  }, [])

  useEffect(() => {
    const unsub = onSnapshot(doc(db, 'settings', 'buyer_order_conditions'), (snap) => {
      const d = snap.data() as Record<string, unknown> | undefined
      if (!d) return
      setOrderConditionsSettings({
        enabled: d.enabled !== false,
        showElectronicPayment: d.showElectronicPayment !== false,
        showInstallment: d.showInstallment !== false,
        showDelivery: d.showDelivery !== false,
        showStorePickup: d.showStorePickup !== false,
      })
    })
    return () => unsub()
  }, [])

  const saveOrderConditionsSettings = async () => {
    try {
      setSavingOrderConditions(true)
      await setDoc(
        doc(db, 'settings', 'buyer_order_conditions'),
        { ...orderConditionsSettings, updatedAt: new Date().toISOString() },
        { merge: true }
      )
      setSnack({ open: true, msg: 'تم حفظ إعدادات شروط الطلب', sev: 'success' })
    } catch (e) {
      console.error(e)
      setSnack({ open: true, msg: 'فشل حفظ إعدادات شروط الطلب', sev: 'error' })
    } finally {
      setSavingOrderConditions(false)
    }
  }

  const updateCategoryCseEngineId = async (categoryId: string, engineId: string) => {
    try {
      await updateDoc(doc(db, 'categories', categoryId), {
        googleCseEngineId: engineId.trim(),
        updatedAt: new Date().toISOString(),
      })
      setAvailableCategories((prev) =>
        prev.map((c) => (c.id === categoryId ? { ...c, googleCseEngineId: engineId.trim() } : c))
      )
      setSnack({ open: true, msg: 'تم حفظ محرك البحث للفئة', sev: 'success' })
    } catch (e) {
      console.error(e)
      setSnack({ open: true, msg: 'فشل حفظ محرك البحث', sev: 'error' })
    }
  }

  const updateCategoryRequestMode = async (categoryId: string, mode: BuyerRequestMode) => {
    try {
      await updateDoc(doc(db, 'categories', categoryId), {
        buyerRequestMode: mode,
        updatedAt: new Date().toISOString(),
      })
      setAvailableCategories((prev) =>
        prev.map((c) => (c.id === categoryId ? { ...c, buyerRequestMode: mode } : c))
      )
      setSnack({ open: true, msg: 'تم حفظ طريقة الطلب للفئة', sev: 'success' })
    } catch (e) {
      console.error(e)
      setSnack({ open: true, msg: 'فشل حفظ طريقة الطلب', sev: 'error' })
    }
  }

  const uploadCategoryIcon = async (categoryId: string, file: File) => {
    try {
      setUploadingCategoryIconId(categoryId)
      const optimized = await compressImageForUpload(file, 'categoryIcon')
      const path = `category_icons/${categoryId}/${Date.now()}_${optimized.name}`
      const storageRef = ref(storage, path)
      await uploadBytes(storageRef, optimized)
      const imageUrl = await getDownloadURL(storageRef)
      await updateDoc(doc(db, 'categories', categoryId), {
        imageUrl,
        updatedAt: new Date().toISOString(),
      })
      setAvailableCategories((prev) =>
        prev.map((c) => (c.id === categoryId ? { ...c, imageUrl } : c))
      )
      setSnack({ open: true, msg: 'تم رفع أيقونة الفئة', sev: 'success' })
    } catch (e) {
      console.error(e)
      setSnack({ open: true, msg: 'فشل رفع الأيقونة', sev: 'error' })
    } finally {
      setUploadingCategoryIconId(null)
    }
  }

  // تحميل إعدادات مدة صلاحية العروض
  useEffect(() => {
    if (availableCategories.length > 0) {
      const expiryUnsub = loadExpirySettings()
      return () => {
        if (expiryUnsub) expiryUnsub()
      }
    }
  }, [availableCategories])

  // تحميل إعدادات نسبة العمولة
  useEffect(() => {
    if (availableCategories.length > 0) {
      const commissionUnsub = loadCommissionSettings()
      return () => {
        if (commissionUnsub) commissionUnsub()
      }
    }
  }, [availableCategories])

  // تحميل إعدادات طرق الدفع
  useEffect(() => {
    const paymentUnsub = loadPaymentMethodsSettings()
    return () => {
      if (paymentUnsub) paymentUnsub()
    }
  }, [])

  // تحميل إعدادات مدة حجز المنتج
  useEffect(() => {
    if (availableCategories.length > 0) {
      const reservationUnsub = loadReservationDurationSettings()
      return () => {
        if (reservationUnsub) reservationUnsub()
      }
    }
  }, [availableCategories])

  // تحميل إعدادات مدة صلاحية الطلبات
  useEffect(() => {
    if (availableCategories.length > 0) {
      const requestExpiryUnsub = loadRequestExpirySettings()
      return () => {
        if (requestExpiryUnsub) requestExpiryUnsub()
      }
    }
  }, [availableCategories])

  // تحميل صور الـ Slider عند تحميل الصفحة
  useEffect(() => {
    loadSliderImages()
  }, [])

  // تحميل إعدادات البحث الذكي من settings/smart_search
  useEffect(() => {
    const ref = doc(db, 'settings', 'smart_search')
    const unsub = onSnapshot(ref, snap => {
      const data = snap.data() || {}
      setSmartSearch({
        wave_interval_minutes: (data.wave_interval_minutes as number) ?? 5,
        initial_merchants_count: (data.initial_merchants_count as number) ?? 5,
        wave_expansion_count: (data.wave_expansion_count as number) ?? 5,
        max_waves: (data.max_waves as number) ?? 5,
        max_offers_limit: (data.max_offers_limit as number) ?? 15,
        min_offers_to_stop_expansion: (data.min_offers_to_stop_expansion as number) ?? 5,
        enforce_governorate_boundary: data.enforce_governorate_boundary === true,
      })
    })
    return () => unsub()
  }, [])

  // تحميل إعدادات تذكير التجار
  useEffect(() => {
    const ref = doc(db, 'settings', 'request_reminders')
    const unsub = onSnapshot(ref, snap => {
      const data = snap.data() || {}
      setRequestReminders({
        enabled: data.enabled !== false,
        first_delay_seconds: (data.first_delay_seconds as number) ?? 60,
        max_reminders_per_seller: (data.max_reminders_per_seller as number) ?? 1,
        second_delay_seconds: (data.second_delay_seconds as number) ?? 180,
        reminder_title_ar: (data.reminder_title_ar as string) ?? 'تذكير: طلب جديد بانتظارك!',
        reminder_body_ar:
          (data.reminder_body_ar as string) ??
          'لم تفتح بعد طلب {productName}',
      })
    })
    return () => unsub()
  }, [])

  // تحميل المظهر الافتراضي للمستخدمين الجدد
  useEffect(() => {
    const ref = doc(db, 'settings', 'app_appearance')
    const unsub = onSnapshot(ref, snap => {
      const v = snap.data()?.defaultThemeMode
      if (v === 'dark' || v === 'system' || v === 'light') {
        setDefaultThemeMode(v)
      }
    })
    return () => unsub()
  }, [])

  const loadExpirySettings = () => {
    try {
      const ref = doc(db, 'settings', 'offer_expiry_settings')
      const unsub = onSnapshot(ref, snap => {
        const data = snap.data() || {}
        const savedExpiry = data.categoryExpiry || {}
        const filteredExpiry: Record<string, number> = {}
        availableCategories.forEach(cat => {
          filteredExpiry[cat.id] = pickCategoryNumericSetting(savedExpiry, cat, 5)
        })
        setCategoryExpiry(filteredExpiry)
        console.log('✅ تم تحميل إعدادات مدة الصلاحية (ترتيب ثابت):', filteredExpiry)
      })
      return unsub
    } catch (e) {
      console.error('خطأ في تحميل إعدادات مدة الصلاحية:', e)
      return null
    }
  }

  const loadAvailableCategories = async () => {
    try {
      console.log('🔄 جاري تحميل الفئات المتاحة من collection "categories" مع الترتيب...')

      // بدون orderBy: استعلام orderBy('order') يستبعد أي مستند ينقصه الحقل order،
      // فتبقى فئات «يتيمة» ظاهرة في التطبيق وغير مرئية هنا. الترتيب يتم بعد الجلب.
      const categoriesSnapshot = await getDocs(collection(db, 'categories'))
      
      if (categoriesSnapshot.docs.length > 0) {
        // استخراج الفئات مع الترتيب وحالة النشاط
        const categories: Array<CategoryItem> = []

        categoriesSnapshot.docs.forEach(catDoc => {
          const data = catDoc.data() as any
          if (data?.name && typeof data.name === 'string' && data.name.trim()) {
            const name = data.name.trim()
            const order = typeof data.order === 'number' ? data.order : 999999
            const isActive = data.isActive !== false // افتراضي نشط للفئات القديمة
            const nameEn = typeof data.nameEn === 'string' && data.nameEn.trim()
              ? data.nameEn.trim()
              : (typeof data.name_en === 'string' && data.name_en.trim() ? data.name_en.trim() : '')
            categories.push({
              name,
              nameEn,
              id: catDoc.id,
              order,
              isActive,
              imageUrl: typeof data.imageUrl === 'string' ? data.imageUrl : '',
              googleCseEngineId:
                typeof data.googleCseEngineId === 'string' ? data.googleCseEngineId : '',
              buyerRequestMode: parseBuyerRequestMode(data.buyerRequestMode),
              subSections: parseCategorySubSections(data.subSections),
              categoryFilters: parseCategoryFilters(data.categoryFilters),
            })
          }
        })

        // ترتيب حسب order ثم أبجدياً كاحتياطي
        categories.sort((a, b) => {
          if (a.order !== b.order) return a.order - b.order
          return a.name.localeCompare(b.name)
        })
        
        console.log('✅ تم تحميل الفئات من collection "categories" مع الترتيب:', categories)
        setAvailableCategories(categories)
        setCategoryDrafts({})
      } else {
        console.log('⚠️ لا توجد فئات في collection "categories"')
        setAvailableCategories([])
        setCategoryDrafts({})
      }
    } catch (e) {
      console.error('❌ خطأ في تحميل الفئات من collection "categories":', e)
      // في حالة عدم وجود index على order، نجرب بدون orderBy
      try {
        const categoriesSnapshot = await getDocs(collection(db, 'categories'))
        const categories: Array<CategoryItem> = []
        categoriesSnapshot.docs.forEach((catDoc, index) => {
          const data = catDoc.data() as any
          if (data?.name && typeof data.name === 'string' && data.name.trim()) {
            categories.push({
              name: data.name.trim(),
              nameEn: (typeof data.nameEn === 'string' && data.nameEn.trim())
                ? data.nameEn.trim()
                : (typeof data.name_en === 'string' && data.name_en.trim() ? data.name_en.trim() : ''),
              id: catDoc.id,
              order: data.order ?? index,
              isActive: data.isActive !== false,
              imageUrl: typeof data.imageUrl === 'string' ? data.imageUrl : '',
              googleCseEngineId:
                typeof data.googleCseEngineId === 'string' ? data.googleCseEngineId : '',
              buyerRequestMode: parseBuyerRequestMode(data.buyerRequestMode),
              subSections: parseCategorySubSections(data.subSections),
              categoryFilters: parseCategoryFilters(data.categoryFilters),
            })
          }
        })
        categories.sort((a, b) => {
          if (a.order !== b.order) return a.order - b.order
          return a.name.localeCompare(b.name)
        })
        setAvailableCategories(categories)
        setCategoryDrafts({})
      } catch (e2) {
        console.error('❌ خطأ في تحميل الفئات بدون orderBy:', e2)
      setAvailableCategories([])
      setCategoryDrafts({})
      }
    }
  }

  // إضافة فئة جديدة إلى collection "categories" (بيئة التطوير / الـ localhost فقط من خلال هذا الـ dashboard)
  const handleAddCategory = async () => {
    const name = newCategoryName.trim()
    if (!name) {
      setSnack({ open: true, msg: 'من فضلك أدخل اسم الفئة', sev: 'error' })
      return
    }

    // منع التكرار في الواجهة
    if (availableCategories.some(cat => cat.name === name)) {
      setSnack({ open: true, msg: 'هذه الفئة موجودة بالفعل', sev: 'error' })
      return
    }

    try {
      setSavingCategory(true)
      // حساب order جديد (آخر ترتيب + 1)
      const maxOrder = availableCategories.length > 0 
        ? Math.max(...availableCategories.map(c => c.order)) 
        : -1
      
      // إنشاء مستند جديد في collection "categories" — فلاتر فارغة (لا نسخ من قطع الغيار)
      const emptyFilters = emptyCategoryFilters()
      const docRef = await addDoc(collection(db, 'categories'), {
        name,
        nameEn: newCategoryNameEn.trim() || name,
        order: maxOrder + 1,
        isActive: true,
        buyerRequestMode: 'search_only',
        subSections: [],
        categoryFilters: emptyFilters,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      })

      // تحديث الحالة محلياً
      setAvailableCategories(prev => [...prev, {
        name,
        nameEn: newCategoryNameEn.trim() || name,
        id: docRef.id,
        order: maxOrder + 1,
        isActive: true,
        buyerRequestMode: 'search_only' as BuyerRequestMode,
        subSections: [],
        categoryFilters: emptyFilters,
      }].sort((a, b) => {
        if (a.order !== b.order) return a.order - b.order
        return a.name.localeCompare(b.name)
      }))
      setNewCategoryName('')
      setNewCategoryNameEn('')
      setSnack({ open: true, msg: 'تم إضافة الفئة بنجاح', sev: 'success' })
    } catch (e: any) {
      console.error('❌ خطأ في إضافة الفئة:', e)
      setSnack({ open: true, msg: e.message || 'فشل في إضافة الفئة', sev: 'error' })
    } finally {
      setSavingCategory(false)
    }
  }

  // حذف فئة من collection "categories"
  const handleDeleteCategory = async (categoryId: string) => {
    try {
      setSavingCategory(true)

      // حذف المستند من collection "categories"
      await deleteDoc(doc(db, 'categories', categoryId))

      // تحديث الحالة محلياً
      setAvailableCategories(prev => prev.filter(c => c.id !== categoryId))
      clearCategoryDraft(categoryId)

      // ملاحظة مهمة:
      // لا نحذف أي إعدادات مرتبطة بهذه الفئة (مدة صلاحية / عمولة / حجز / الخ) تلقائياً هنا
      // حتى لا نتسبب في حذف بيانات بالخطأ. سيتم تجاهل الفئات غير الموجودة مسبقاً
      // بفضل منطق الفلترة في دوال التحميل أعلاه.

      setSnack({ open: true, msg: 'تم حذف الفئة بنجاح', sev: 'success' })
    } catch (e: any) {
      console.error('❌ خطأ في حذف الفئة:', e)
      setSnack({ open: true, msg: e.message || 'فشل في حذف الفئة', sev: 'error' })
    } finally {
      setSavingCategory(false)
    }
  }

  // نقل فئة لأعلى
  const handleMoveCategoryUp = async (index: number) => {
    if (index === 0) return
    
    const categories = [...availableCategories]
    const temp = categories[index]
    categories[index] = categories[index - 1]
    categories[index - 1] = temp
    
    // تحديث الترتيب
    categories.forEach((cat, idx) => {
      cat.order = idx
    })
    
    setAvailableCategories(categories)
    await saveCategoryOrder(categories)
  }

  // نقل فئة لأسفل
  const handleMoveCategoryDown = async (index: number) => {
    if (index === availableCategories.length - 1) return
    
    const categories = [...availableCategories]
    const temp = categories[index]
    categories[index] = categories[index + 1]
    categories[index + 1] = temp
    
    // تحديث الترتيب
    categories.forEach((cat, idx) => {
      cat.order = idx
    })
    
    setAvailableCategories(categories)
    await saveCategoryOrder(categories)
  }

  // تغيير حالة الفئة (نشط / غير نشط). الفئة غير النشطة لا تظهر للمشتري في قائمة "نوع المتجر".
  const handleToggleCategoryActive = async (categoryId: string) => {
    const cat = availableCategories.find(c => c.id === categoryId)
    if (!cat) return
    const newActive = !cat.isActive
    try {
      setSavingCategory(true)
      await updateDoc(doc(db, 'categories', categoryId), {
        isActive: newActive,
        updatedAt: new Date().toISOString(),
      })
      setAvailableCategories(prev =>
        prev.map(c => (c.id === categoryId ? { ...c, isActive: newActive } : c))
      )
      setSnack({
        open: true,
        msg: newActive ? 'تم تفعيل الفئة' : 'تم إلغاء تفعيل الفئة (لن تظهر للمشتري في قائمة نوع المتجر)',
        sev: 'success',
      })
    } catch (e: any) {
      console.error('❌ خطأ في تغيير حالة الفئة:', e)
      setSnack({ open: true, msg: e.message || 'فشل في تغيير الحالة', sev: 'error' })
    } finally {
      setSavingCategory(false)
    }
  }

  const persistCategoryFilters = async (
    categoryId: string,
    config: CategoryFiltersConfig,
  ) => {
    const serialized = serializeCategoryFilters(config)
    const syncedSubSections = filter3OptionsToSubSections(serialized.filter3.options)
    await updateDoc(doc(db, 'categories', categoryId), {
      categoryFilters: serialized,
      subSections: syncedSubSections,
      updatedAt: new Date().toISOString(),
    })
    setAvailableCategories(prev =>
      prev.map(c =>
        c.id === categoryId
          ? { ...c, categoryFilters: serialized, subSections: syncedSubSections }
          : c,
      ),
    )
  }

  const handleSaveCategoryFilters = async (
    categoryId: string,
    config: CategoryFiltersConfig,
  ) => {
    try {
      setSavingCategoryFiltersId(categoryId)
      await persistCategoryFilters(categoryId, config)
      setSnack({ open: true, msg: 'تم حفظ فلاتر الفئة (ومزامنة الأقسام من الفلتر 3)', sev: 'success' })
    } catch (e: any) {
      setSnack({ open: true, msg: e.message || 'فشل حفظ فلاتر الفئة', sev: 'error' })
      throw e
    } finally {
      setSavingCategoryFiltersId(null)
    }
  }

  // حفظ ترتيب الفئات
  const saveCategoryOrder = async (categories: Array<CategoryItem>) => {
    try {
      setSavingCategoryOrder(true)
      
      // تحديث order لكل فئة في Firestore
      const updatePromises = categories.map(cat => 
        updateDoc(doc(db, 'categories', cat.id), {
          order: cat.order,
          updatedAt: new Date().toISOString()
        })
      )
      
      await Promise.all(updatePromises)
      setSnack({ open: true, msg: 'تم حفظ ترتيب الفئات بنجاح', sev: 'success' })
    } catch (e: any) {
      console.error('❌ خطأ في حفظ ترتيب الفئات:', e)
      setSnack({ open: true, msg: e.message || 'فشل في حفظ ترتيب الفئات', sev: 'error' })
      // إعادة تحميل الفئات في حالة الخطأ
      loadAvailableCategories()
    } finally {
      setSavingCategoryOrder(false)
    }
  }

  const clearCategoryDraft = (categoryId: string) => {
    setCategoryDrafts(prev => {
      if (!(categoryId in prev)) return prev
      const next = { ...prev }
      delete next[categoryId]
      return next
    })
  }

  // تعديل اسم الفئة
  const handleRenameCategory = async (categoryId: string, newName: string) => {
    const name = newName.trim()
    if (!name) {
      setSnack({ open: true, msg: 'اسم الفئة لا يمكن أن يكون فارغاً', sev: 'error' })
      return
    }

    // منع التكرار بالاسم
    if (availableCategories.some(cat => cat.id !== categoryId && cat.name === name)) {
      setSnack({ open: true, msg: 'هناك فئة أخرى بنفس الاسم', sev: 'error' })
      return
    }

    // الحصول على الاسم القديم للفئة قبل التعديل حتى نستخدمه في تحديث باقي الإعدادات والمستخدمين
    const oldCategory = availableCategories.find(cat => cat.id === categoryId)
    const oldName = oldCategory?.name
    const oldNameEn = oldCategory?.nameEn ?? ''

    try {
      setSavingCategory(true)
      const categoryUpdate: Record<string, unknown> = {
        name,
        updatedAt: new Date().toISOString(),
      }
      if (oldName && oldName !== name) {
        categoryUpdate.previousNames = arrayUnion(oldName)
      }
      await updateDoc(doc(db, 'categories', categoryId), categoryUpdate)

      // تحديث الحالة محلياً
      setAvailableCategories(prev =>
        prev.map(cat => (cat.id === categoryId ? { ...cat, name } : cat))
      )
      clearCategoryDraft(categoryId)

      // إعدادات الفئات تُخزَّن بمفتاح categoryId (لا يتغيّر عند تعديل الاسم). نحدّث تجار users فقط.
      if (oldName && oldName !== name) {
        console.log(`🔄 تحديث ربط التجار بعد تغيير اسم الفئة: "${oldName}" -> "${name}"`)
        await renameCategoryForSellers(categoryId, oldName, name, oldNameEn, oldNameEn)
        try {
          const recentSnap = await getDocs(
            query(
              collection(db, 'browse_recent_products'),
              where('productCategoryId', '==', categoryId)
            )
          )
          await Promise.all(
            recentSnap.docs.map(d => updateDoc(d.ref, { productCategory: name }))
          )
        } catch (recentErr) {
          console.warn('⚠️ تعذر تحديث منتجات «تم طلبه مؤخراً» بعد إعادة التسمية:', recentErr)
        }
      }

      setSnack({ open: true, msg: 'تم تعديل اسم الفئة بنجاح', sev: 'success' })
    } catch (e: any) {
      console.error('❌ خطأ في تعديل اسم الفئة:', e)
      setSnack({ open: true, msg: e.message || 'فشل في تعديل اسم الفئة', sev: 'error' })
      // إعادة تحميل الفئات في حالة الخطأ
      loadAvailableCategories()
    } finally {
      setSavingCategory(false)
    }
  }

  const handleRenameCategoryEn = async (categoryId: string, newNameEn: string) => {
    const englishName = newNameEn.trim()
    const oldCategory = availableCategories.find(cat => cat.id === categoryId)
    const oldArName = oldCategory?.name ?? ''
    const oldEnName = oldCategory?.nameEn ?? ''
    try {
      setSavingCategory(true)
      const categoryEnUpdate: Record<string, unknown> = {
        nameEn: englishName,
        updatedAt: new Date().toISOString(),
      }
      if (oldEnName && oldEnName !== englishName) {
        categoryEnUpdate.previousNames = arrayUnion(oldEnName)
      }
      await updateDoc(doc(db, 'categories', categoryId), categoryEnUpdate)
      setAvailableCategories(prev =>
        prev.map(cat => (cat.id === categoryId ? { ...cat, nameEn: englishName } : cat))
      )
      setCategoryEnDrafts(prev => {
        const next = { ...prev }
        delete next[categoryId]
        return next
      })
      await renameCategoryForSellers(categoryId, oldArName, oldArName, oldEnName, englishName)
      setSnack({ open: true, msg: 'تم تحديث الاسم الإنجليزي بنجاح', sev: 'success' })
    } catch (e: any) {
      console.error('❌ خطأ في تعديل الاسم الإنجليزي للفئة:', e)
      setSnack({ open: true, msg: e.message || 'فشل في تعديل الاسم الإنجليزي', sev: 'error' })
    } finally {
      setSavingCategory(false)
    }
  }

  const loadCommissionSettings = () => {
    try {
      const ref = doc(db, 'settings', 'commission_rates')
      const unsub = onSnapshot(ref, snap => {
        const data = snap.data() || {}
        const rates: Record<string, number> = {}
        // ترتيب المفاتيح يتبع ترتيب الفئات المتاحة فقط لتفادي تغيّر ترتيب العرض بعد الحفظ
        availableCategories.forEach(cat => {
          rates[cat.id] = pickCommissionPercentFromDoc(data, cat)
        })
        setCommissionRates(rates)
        console.log('✅ تم تحميل إعدادات نسبة العمولة مع فلترة الفئات:', rates)
      })
      return unsub
    } catch (e) {
      console.error('خطأ في تحميل إعدادات نسبة العمولة:', e)
      return null
    }
  }

  const loadPaymentMethodsSettings = () => {
    try {
      const ref = doc(db, 'settings', 'payment_methods')
      const unsub = onSnapshot(ref, snap => {
        const data = snap.data() || {}
        setPaymentMethods(prev => ({
          ...prev,
          ...data
        }))
      })
      return unsub
    } catch (e) {
      console.error('خطأ في تحميل إعدادات طرق الدفع:', e)
      return null
    }
  }

  const loadReservationDurationSettings = () => {
    try {
      const ref = doc(db, 'settings', 'reservation_duration_settings')
      const unsub = onSnapshot(ref, snap => {
        const data = snap.data() || {}
        const savedDuration = data.reservationDuration || {}
        const filteredDuration: Record<string, number> = {}
        availableCategories.forEach(cat => {
          const value =
            savedDuration[cat.id] ??
            savedDuration[cat.name] ??
            (cat.nameEn && savedDuration[cat.nameEn] !== undefined ? savedDuration[cat.nameEn] : undefined)
          if (value !== undefined && typeof value === 'number') {
            filteredDuration[cat.id] = value > 60 ? Math.round(value / 60) : value
          } else {
            filteredDuration[cat.id] = 1
          }
        })
        setReservationDuration(filteredDuration)
        console.log('✅ تم تحميل إعدادات مدة حجز المنتج (ترتيب ثابت):', filteredDuration)
      })
      return unsub
    } catch (e) {
      console.error('خطأ في تحميل إعدادات مدة حجز المنتج:', e)
      return null
    }
  }

  const loadRequestExpirySettings = () => {
    try {
      const ref = doc(db, 'settings', 'request_expiry_settings')
      const unsub = onSnapshot(ref, snap => {
        const data = snap.data() || {}
        const savedExpiry = data.categoryExpiry || {}
        const filteredExpiry: Record<string, number> = {}
        availableCategories.forEach(cat => {
          filteredExpiry[cat.id] = pickCategoryNumericSetting(savedExpiry, cat, 24)
        })
        setRequestExpiry(filteredExpiry)
        console.log('✅ تم تحميل إعدادات مدة صلاحية الطلبات (ترتيب ثابت):', filteredExpiry)
      })
      return unsub
    } catch (e) {
      console.error('خطأ في تحميل إعدادات مدة صلاحية الطلبات:', e)
      return null
    }
  }

  const save = async () => {
    if (minRangeKm <= 0 || maxRangeKm <= 0 || maxRangeKm < minRangeKm) {
      setSnack({open: true, msg: 'القيم غير صحيحة. تأكد من أن الحد الأقصى أكبر من الحد الأدنى.', sev: 'error'})
      return
    }
    try {
      setSaving(true)
      const payload = { minRangeKm, maxRangeKm, allowedSearchMode, updatedAt: new Date().toISOString() }
      // Always write to settings/*
      await setDoc(doc(db, 'settings', 'global'), payload, { merge: true })
      // Best-effort write to admin-only app_settings/* without blocking save
      try {
        await setDoc(doc(db, 'app_settings', 'global'), payload, { merge: true })
      } catch (err) {
        console.warn('Skipping app_settings write (insufficient permissions):', err)
      }
      setSnack({open: true, msg: 'تم الحفظ بنجاح', sev: 'success'})
    } catch (e: any) {
      setSnack({open: true, msg: e.message || 'فشل الحفظ', sev: 'error'})
    } finally {
      setSaving(false)
    }
  }

  const saveMaxConcurrentRequests = async () => {
    if (maxConcurrentRequests <= 0 || maxConcurrentRequests > 10) {
      setSnack({open: true, msg: 'القيمة غير صحيحة. يجب أن تكون بين 1 و 10', sev: 'error'})
      return
    }
    try {
      setSavingMaxRequests(true)
      const payload = { maxConcurrentRequests, updatedAt: new Date().toISOString() }
      await setDoc(doc(db, 'settings', 'global'), payload, { merge: true })
      try {
        await setDoc(doc(db, 'app_settings', 'global'), payload, { merge: true })
      } catch (err) {
        console.warn('Skipping app_settings write (insufficient permissions):', err)
      }
      setSnack({open: true, msg: 'تم حفظ إعدادات عدد الطلبات المسموح بنجاح', sev: 'success'})
    } catch (e: any) {
      setSnack({open: true, msg: e.message || 'فشل في حفظ إعدادات عدد الطلبات المسموح', sev: 'error'})
    } finally {
      setSavingMaxRequests(false)
    }
  }

  const saveBuyerRestrictions = async () => {
    try {
      setSavingBuyerRestrictions(true)
      const payload = {
        buyerRestrictions: {
          ...buyerRestrictions,
          updatedAt: new Date().toISOString(),
        },
        updatedAt: new Date().toISOString(),
      }
      await setDoc(doc(db, 'settings', 'global'), payload, { merge: true })
      try {
        await setDoc(doc(db, 'app_settings', 'global'), payload, { merge: true })
      } catch (err) {
        console.warn('Skipping app_settings write (insufficient permissions):', err)
      }
      setSnack({open: true, msg: 'تم حفظ إعدادات قيود المشترين بنجاح', sev: 'success'})
    } catch (e: any) {
      setSnack({open: true, msg: e.message || 'فشل في حفظ إعدادات قيود المشترين', sev: 'error'})
    } finally {
      setSavingBuyerRestrictions(false)
    }
  }

  const saveExpirySettings = async () => {
    try {
      setSavingExpiry(true)
      const expiryToSave: Record<string, number> = {}
      availableCategories.forEach(cat => {
        expiryToSave[cat.id] = categoryExpiry[cat.id] ?? 5
      })
      const payload = { categoryExpiry: expiryToSave, updatedAt: new Date().toISOString() }
      await setDoc(doc(db, 'settings', 'offer_expiry_settings'), payload, { merge: true })
      try {
        await setDoc(doc(db, 'app_settings', 'offer_expiry_settings'), payload, { merge: true })
      } catch (err) {
        console.warn('Skipping app_settings write (insufficient permissions):', err)
      }
      setSnack({open: true, msg: 'تم حفظ إعدادات مدة الصلاحية بنجاح', sev: 'success'})
    } catch (e: any) {
      setSnack({open: true, msg: e.message || 'فشل في حفظ إعدادات مدة الصلاحية', sev: 'error'})
    } finally {
      setSavingExpiry(false)
    }
  }

  const updateCategoryExpiry = (category: string, minutes: number) => {
    setCategoryExpiry(prev => ({
      ...prev,
      [category]: minutes
    }))
  }

  const addNewCategory = (category: string) => {
    if (!categoryExpiry[category]) {
      setCategoryExpiry(prev => ({
        ...prev,
        [category]: 5 // القيمة الافتراضية
      }))
    }
  }

  const saveCommissionSettings = async () => {
    try {
      setSavingCommission(true)
      const ratesToSave: Record<string, number> = {}
      // حفظ بترتيب ثابت (ترتيب الفئات المتاحة فقط) لتفادي تغيّر ترتيب العرض
      availableCategories.forEach(cat => {
        ratesToSave[cat.id] = (commissionRates[cat.id] ?? 0) / 100
      })
      await setDoc(doc(db, 'settings', 'commission_rates'), { ...ratesToSave, updatedAt: new Date().toISOString() }, { merge: true })
      setSnack({open: true, msg: 'تم حفظ إعدادات نسبة العمولة بنجاح', sev: 'success'})
    } catch (e: any) {
      setSnack({open: true, msg: e.message || 'فشل في حفظ إعدادات نسبة العمولة', sev: 'error'})
    } finally {
      setSavingCommission(false)
    }
  }

  const savePaymentMethodsSettings = async () => {
    try {
      setSavingPaymentMethods(true)
      const payload = { ...paymentMethods, updatedAt: new Date().toISOString() }
      await setDoc(doc(db, 'settings', 'payment_methods'), payload, { merge: true })
      try {
        await setDoc(doc(db, 'app_settings', 'payment_methods'), payload, { merge: true })
      } catch (err) {
        console.warn('Skipping app_settings write (insufficient permissions):', err)
      }
      
      setSnack({open: true, msg: 'تم حفظ إعدادات طرق الدفع بنجاح', sev: 'success'})
    } catch (e: any) {
      setSnack({open: true, msg: e.message || 'فشل في حفظ إعدادات طرق الدفع', sev: 'error'})
    } finally {
      setSavingPaymentMethods(false)
    }
  }

  const updateCommissionRate = (category: string, percentage: number) => {
    setCommissionRates(prev => ({
      ...prev,
      [category]: percentage
    }))
  }

  const addNewCommissionCategory = (category: string) => {
    if (!(category in commissionRates)) {
      setCommissionRates(prev => ({
        ...prev,
        [category]: 0 // الوضع الافتراضي 0%
      }))
    }
  }

  const updatePaymentMethod = (method: string, enabled: boolean) => {
    setPaymentMethods(prev => ({
      ...prev,
      [method]: enabled
    }))
  }

  // تحميل صور الـ Slider من Firestore
  const loadSliderImages = async () => {
    try {
      console.log('🔄 جاري تحميل صور الـ Slider...')
      const sliderImagesRef = collection(db, 'slider_images')
      const snapshot = await getDocs(query(sliderImagesRef, orderBy('order')))
      const images: Array<{id: string, imageUrl: string, order: number, isActive: boolean}> = []
      snapshot.docs.forEach(doc => {
        const data = doc.data()
        if (data.imageUrl) {
          images.push({
            id: doc.id,
            imageUrl: data.imageUrl,
            order: data.order ?? 0,
            isActive: data.isActive !== false // افتراضياً نشط إذا لم يُحدد
          })
        }
      })
      setSliderImages(images)
      console.log('✅ تم تحميل صور الـ Slider:', images.length)
    } catch (e) {
      console.error('❌ خطأ في تحميل صور الـ Slider:', e)
      // في حالة عدم وجود index على order، نجرب بدون orderBy
      try {
        const snapshot = await getDocs(collection(db, 'slider_images'))
        const images: Array<{id: string, imageUrl: string, order: number, isActive: boolean}> = []
        snapshot.docs.forEach((doc, index) => {
          const data = doc.data()
          if (data.imageUrl) {
            images.push({
              id: doc.id,
              imageUrl: data.imageUrl,
              order: data.order ?? index,
              isActive: data.isActive !== false // افتراضياً نشط إذا لم يُحدد
            })
          }
        })
        images.sort((a, b) => a.order - b.order)
        setSliderImages(images)
        console.log('✅ تم تحميل صور الـ Slider (بدون orderBy):', images.length)
      } catch (e2) {
        console.error('❌ خطأ في تحميل صور الـ Slider بدون orderBy:', e2)
        setSliderImages([])
      }
    }
  }

  // رفع صورة جديدة للـ Slider
  const handleUploadSliderImage = async (file: File) => {
    try {
      setUploadingSliderImage(true)
      console.log('📤 بدء رفع صورة الـ Slider...', file.name)
      
      // رفع الصورة إلى Firebase Storage
      const optimized = await compressImageForUpload(file, 'slider')
      const timestamp = Date.now()
      const fileName = `slider_${timestamp}_${optimized.name.replace(/[^a-zA-Z0-9.-]/g, '_')}`
      const storageRef = ref(storage, `slider_images/${fileName}`)
      
      await uploadBytes(storageRef, optimized)
      console.log('✅ تم رفع الصورة بنجاح')
      
      // الحصول على رابط التحميل
      const imageUrl = await getDownloadURL(storageRef)
      console.log('✅ تم الحصول على رابط التحميل:', imageUrl)
      
      // حساب order جديد
      const maxOrder = sliderImages.length > 0 
        ? Math.max(...sliderImages.map(img => img.order)) 
        : 0
      
      // حفظ في Firestore
      await addDoc(collection(db, 'slider_images'), {
        imageUrl,
        order: maxOrder + 1,
        isActive: true, // افتراضياً الصورة الجديدة نشطة
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      })
      
      console.log('✅ تم حفظ رابط الصورة في Firestore')
      
      // إعادة تحميل القائمة
      await loadSliderImages()
      setSnack({ open: true, msg: 'تم رفع الصورة بنجاح', sev: 'success' })
    } catch (e: any) {
      console.error('❌ خطأ في رفع الصورة:', e)
      setSnack({ open: true, msg: e.message || 'فشل في رفع الصورة', sev: 'error' })
    } finally {
      setUploadingSliderImage(false)
    }
  }

  // تحديث حالة الصورة (نشط/معطل)
  const handleToggleSliderImageActive = async (imageId: string, isActive: boolean) => {
    try {
      setUpdatingSliderImage(imageId)
      await updateDoc(doc(db, 'slider_images', imageId), {
        isActive,
        updatedAt: new Date().toISOString()
      })
      await loadSliderImages()
      setSnack({ open: true, msg: isActive ? 'تم تفعيل الصورة' : 'تم تعطيل الصورة', sev: 'success' })
    } catch (e: any) {
      console.error('❌ خطأ في تحديث حالة الصورة:', e)
      setSnack({ open: true, msg: e.message || 'فشل في تحديث حالة الصورة', sev: 'error' })
    } finally {
      setUpdatingSliderImage(null)
    }
  }

  // حذف صورة من الـ Slider
  const handleDeleteSliderImage = async (imageId: string) => {
    if (!confirm('هل أنت متأكد من حذف هذه الصورة؟')) {
      return
    }
    
    try {
      await deleteDoc(doc(db, 'slider_images', imageId))
      await loadSliderImages()
      setSnack({ open: true, msg: 'تم حذف الصورة بنجاح', sev: 'success' })
    } catch (e: any) {
      console.error('❌ خطأ في حذف الصورة:', e)
      setSnack({ open: true, msg: e.message || 'فشل في حذف الصورة', sev: 'error' })
    }
  }

  // ترتيب صور الـ Slider (أعلى/أسفل)
  const handleMoveSliderImage = async (imageId: string, direction: 'up' | 'down') => {
    try {
      const image = sliderImages.find(img => img.id === imageId)
      if (!image) return
      
      const currentIndex = sliderImages.findIndex(img => img.id === imageId)
      const newIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1
      
      if (newIndex < 0 || newIndex >= sliderImages.length) return
      
      const otherImage = sliderImages[newIndex]
      
      // تبديل الترتيب
      await updateDoc(doc(db, 'slider_images', imageId), {
        order: otherImage.order,
        updatedAt: new Date().toISOString()
      })
      await updateDoc(doc(db, 'slider_images', otherImage.id), {
        order: image.order,
        updatedAt: new Date().toISOString()
      })
      
      await loadSliderImages()
    } catch (e: any) {
      console.error('❌ خطأ في ترتيب الصورة:', e)
      setSnack({ open: true, msg: e.message || 'فشل في ترتيب الصورة', sev: 'error' })
    }
  }

  const saveReservationDurationSettings = async () => {
    try {
      setSavingReservationDuration(true)
      const reservationDurationInMinutes: Record<string, number> = {}
      availableCategories.forEach(cat => {
        reservationDurationInMinutes[cat.id] = (reservationDuration[cat.id] ?? 1) * 60
      })
      const payload = { reservationDuration: reservationDurationInMinutes, updatedAt: new Date().toISOString() }
      await setDoc(doc(db, 'settings', 'reservation_duration_settings'), payload, { merge: true })
      try {
        await setDoc(doc(db, 'app_settings', 'reservation_duration_settings'), payload, { merge: true })
      } catch (err) {
        console.warn('Skipping app_settings write (insufficient permissions):', err)
      }
      
      setSnack({open: true, msg: 'تم حفظ إعدادات مدة حجز المنتج بنجاح', sev: 'success'})
    } catch (e: any) {
      setSnack({open: true, msg: e.message || 'فشل في حفظ إعدادات مدة حجز المنتج', sev: 'error'})
    } finally {
      setSavingReservationDuration(false)
    }
  }

  const updateReservationDuration = (category: string, hours: number) => {
    setReservationDuration(prev => ({
      ...prev,
      [category]: hours
    }))
  }

  const addNewReservationCategory = (category: string) => {
    if (!reservationDuration[category]) {
      setReservationDuration(prev => ({
        ...prev,
        [category]: 1 // القيمة الافتراضية: ساعة واحدة
      }))
    }
  }

  const saveRequestExpirySettings = async () => {
    try {
      setSavingRequestExpiry(true)
      const expiryToSave: Record<string, number> = {}
      availableCategories.forEach(cat => {
        expiryToSave[cat.id] = requestExpiry[cat.id] ?? 24
      })
      const payload = { categoryExpiry: expiryToSave, updatedAt: new Date().toISOString() }
      await setDoc(doc(db, 'settings', 'request_expiry_settings'), payload, { merge: true })
      try {
        await setDoc(doc(db, 'app_settings', 'request_expiry_settings'), payload, { merge: true })
      } catch (err) {
        console.warn('Skipping app_settings write (insufficient permissions):', err)
      }
      setSnack({open: true, msg: 'تم حفظ إعدادات مدة صلاحية الطلبات بنجاح', sev: 'success'})
    } catch (e: any) {
      setSnack({open: true, msg: e.message || 'فشل في حفظ إعدادات مدة صلاحية الطلبات', sev: 'error'})
    } finally {
      setSavingRequestExpiry(false)
    }
  }

  const updateRequestExpiry = (category: string, hours: number) => {
    setRequestExpiry(prev => ({
      ...prev,
      [category]: hours
    }))
  }

  const addNewRequestExpiryCategory = (category: string) => {
    if (!requestExpiry[category]) {
      setRequestExpiry(prev => ({
        ...prev,
        [category]: 24 // القيمة الافتراضية: 24 ساعة
      }))
    }
  }

  const resetCommissionToDefaults = async () => {
    try {
      setSavingCommission(true)
      const defaultRates: Record<string, number> = {}
      availableCategories.forEach(cat => { defaultRates[cat.id] = 0 })
      await setDoc(doc(db, 'settings', 'commission_rates'), { ...defaultRates, updatedAt: new Date().toISOString() }, { merge: true })
      setCommissionRates(defaultRates)
      setSnack({open: true, msg: 'تم إعادة تعيين إعدادات نسبة العمولة إلى 0%', sev: 'success'})
    } catch (e: any) {
      setSnack({open: true, msg: e.message || 'فشل في إعادة التعيين', sev: 'error'})
    } finally {
      setSavingCommission(false)
    }
  }

  return (
    <Box>
      <Typography variant="h4" sx={{ mb: 3, fontWeight: 'bold' }}>الإعدادات</Typography>
      
      <Tabs value={currentTab} onChange={(e, v) => setCurrentTab(v)} sx={{ mb: 3, borderBottom: 1, borderColor: 'divider' }}>
        <Tab label="الفئات" />
        <Tab label="إعدادات عامة" />
        <Tab label="إعدادات الفئات" />
        <Tab label="طرق الدفع" />
        <Tab label="صور الـ Slider" />
        <Tab label="البحث الذكي" />
        <Tab label="شروط الطلب" />
      </Tabs>

      {/* تبويب الفئات */}
      {currentTab === 0 && (
        <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" sx={{ mb: 2 }}>إدارة الفئات المتاحة في التطبيق</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            من هنا يمكنك إضافة أو حذف الفئات الرئيسية التي يعتمد عليها التطبيق (مثل الموبايلات، الأجهزة الكبيرة، إلخ).
            التعديلات تُحفظ مباشرة في قاعدة البيانات وتظهر في التطبيق فوراً.
          </Typography>

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 2, maxWidth: 600, alignItems: 'center' }}>
            <TextField
              label="اسم الفئة الجديدة"
              value={newCategoryName}
              onChange={e => setNewCategoryName(e.target.value)}
              sx={{ flex: 1 }}
            />
            <TextField
              label="الاسم الإنجليزي (اختياري)"
              value={newCategoryNameEn}
              onChange={e => setNewCategoryNameEn(e.target.value)}
              sx={{ flex: 1 }}
            />
            <Button
              variant="contained"
              onClick={handleAddCategory}
              disabled={savingCategory}
            >
              {savingCategory ? 'جارِ الحفظ...' : 'إضافة فئة'}
            </Button>
          </Stack>

          <Divider sx={{ mb: 2 }} />

          <Typography variant="subtitle2" sx={{ mb: 1 }}>الفئات الحالية (يمكنك ترتيبها باستخدام الأزرار):</Typography>
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 2 }}>
            الفئة غير النشطة لا تظهر للمشتري في شاشة «حدد نوع المنتج». يمكن رفع أيقونة لكل فئة وتحديد Google CSE Engine ID وطريقة الطلب (بحث / يدوي / الاثنان).
          </Typography>
          {availableCategories.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              لا توجد فئات حالياً. قم بإضافة فئة جديدة من الحقل أعلاه.
            </Typography>
          ) : (
            <Stack spacing={1}>
              {availableCategories.map((category, index) => (
                <Paper
                  key={category.id}
                  elevation={1}
                  sx={{
                    p: 1.5,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'stretch',
                    gap: 1,
                    opacity: category.isActive ? 1 : 0.85,
                    '&:hover': { bgcolor: 'action.hover' }
                  }}
                >
                  <Stack direction="row" alignItems="center" spacing={2}>
                  <Typography sx={{ minWidth: 30, fontWeight: 'medium' }}>
                    {index + 1}.
                  </Typography>
                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ flex: 1 }}>
                    <TextField
                      size="small"
                      variant="outlined"
                      label="الاسم العربي"
                      value={categoryDrafts[category.id] ?? category.name}
                      onChange={e => {
                        const value = e.target.value
                        setCategoryDrafts(prev => ({ ...prev, [category.id]: value }))
                      }}
                      onBlur={() => {
                        const currentValue = (categoryDrafts[category.id] ?? category.name).trim()
                        if (!currentValue) {
                          clearCategoryDraft(category.id)
                          return
                        }
                        if (currentValue !== category.name) {
                          handleRenameCategory(category.id, currentValue)
                        } else {
                          clearCategoryDraft(category.id)
                        }
                      }}
                      sx={{ flex: 1 }}
                    />
                    <TextField
                      size="small"
                      variant="outlined"
                      label="English name"
                      value={categoryEnDrafts[category.id] ?? (category.nameEn ?? '')}
                      onChange={e => {
                        const value = e.target.value
                        setCategoryEnDrafts(prev => ({ ...prev, [category.id]: value }))
                      }}
                      onBlur={() => {
                        const currentEnValue = (categoryEnDrafts[category.id] ?? (category.nameEn ?? '')).trim()
                        if (currentEnValue !== (category.nameEn ?? '')) {
                          handleRenameCategoryEn(category.id, currentEnValue)
                        }
                      }}
                      sx={{ flex: 1 }}
                    />
                    <TextField
                      size="small"
                      label="Google CSE Engine ID"
                      placeholder="افتراضي من الإعدادات العامة"
                      value={categoryCseDrafts[category.id] ?? (category.googleCseEngineId ?? '')}
                      onChange={(e) =>
                        setCategoryCseDrafts((prev) => ({ ...prev, [category.id]: e.target.value }))
                      }
                      onBlur={() => {
                        const v = (categoryCseDrafts[category.id] ?? category.googleCseEngineId ?? '').trim()
                        if (v !== (category.googleCseEngineId ?? '')) {
                          updateCategoryCseEngineId(category.id, v)
                        }
                      }}
                      sx={{ minWidth: 220 }}
                    />
                    <FormControl size="small" sx={{ minWidth: 200 }}>
                      <InputLabel id={`req-mode-${category.id}`}>طريقة الطلب</InputLabel>
                      <Select
                        labelId={`req-mode-${category.id}`}
                        label="طريقة الطلب"
                        value={category.buyerRequestMode ?? 'search_only'}
                        onChange={(e) =>
                          updateCategoryRequestMode(category.id, e.target.value as BuyerRequestMode)
                        }
                      >
                        <MenuItem value="search_only">بحث نصي فقط</MenuItem>
                        <MenuItem value="manual_only">إدخال يدوي فقط</MenuItem>
                        <MenuItem value="both">الاثنان معاً</MenuItem>
                      </Select>
                    </FormControl>
                    <Button
                      variant="outlined"
                      size="small"
                      component="label"
                      disabled={uploadingCategoryIconId === category.id}
                      startIcon={<CloudUpload />}
                    >
                      {category.imageUrl ? 'تغيير الأيقونة' : 'أيقونة الفئة'}
                      <input
                        type="file"
                        accept="image/*"
                        hidden
                        onChange={(e) => {
                          const f = e.target.files?.[0]
                          if (f) void uploadCategoryIcon(category.id, f)
                          e.target.value = ''
                        }}
                      />
                    </Button>
                    {category.imageUrl ? (
                      <Box
                        component="img"
                        src={category.imageUrl}
                        alt=""
                        sx={{ width: 40, height: 40, borderRadius: 1, objectFit: 'cover' }}
                      />
                    ) : null}
                  </Stack>
                  <FormControlLabel
                    control={
                      <Switch
                        size="small"
                        checked={category.isActive}
                        onChange={() => handleToggleCategoryActive(category.id)}
                        disabled={savingCategory}
                        color="primary"
                      />
                    }
                    label={category.isActive ? 'نشط' : 'غير نشط'}
                    sx={{ ml: 0 }}
                  />
                  <Stack direction="row" spacing={0.5}>
                    <IconButton
                      size="small"
                      onClick={() => handleMoveCategoryUp(index)}
                      disabled={index === 0 || savingCategoryOrder}
                      color="primary"
                    >
                      <ArrowUpward fontSize="small" />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => handleMoveCategoryDown(index)}
                      disabled={index === availableCategories.length - 1 || savingCategoryOrder}
                      color="primary"
                    >
                      <ArrowDownward fontSize="small" />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => handleDeleteCategory(category.id)}
                      disabled={savingCategory}
                      color="error"
                    >
                      <Delete fontSize="small" />
                    </IconButton>
                  </Stack>
                  </Stack>
                  <Accordion
                    disableGutters
                    elevation={0}
                    sx={{
                      width: '100%',
                      mt: 1,
                      bgcolor: 'transparent',
                      '&:before': { display: 'none' },
                    }}
                  >
                    <AccordionSummary expandIcon={<ExpandMore />}>
                      <Typography variant="body2" fontWeight={600}>
                        فلاتر الفئة (حتى 3)
                        {(() => {
                          const n = [
                            category.categoryFilters.filter1,
                            category.categoryFilters.filter2,
                            category.categoryFilters.filter3,
                          ].filter(f => f.enabled).length
                          return n > 0 ? ` — ${n} مفعّل` : ''
                        })()}
                      </Typography>
                    </AccordionSummary>
                    <AccordionDetails sx={{ pt: 0 }}>
                      <CategoryFiltersEditor
                        value={category.categoryFilters}
                        saving={savingCategoryFiltersId === category.id}
                        conditionPreset={categoryConditionPreset(category)}
                        onSave={config => handleSaveCategoryFilters(category.id, config)}
                      />
                    </AccordionDetails>
                  </Accordion>
                </Paper>
              ))}
            </Stack>
          )}
        </CardContent>
      </Card>
      )}

      {/* تبويب الإعدادات العامة */}
      {currentTab === 1 && (
        <>
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" sx={{ mb: 1 }}>المظهر الافتراضي للتطبيق</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            هذا الاختيار يظهر للمستخدم أول مرة يفتح فيها التطبيق. من غيّر المظهر بنفسه من داخل التطبيق يبقى على اختياره.
          </Typography>
          <FormControl>
            <FormLabel sx={{ mb: 1 }}>الوضع عند أول تشغيل</FormLabel>
            <RadioGroup
              row
              value={defaultThemeMode}
              onChange={e =>
                setDefaultThemeMode(e.target.value as 'light' | 'dark' | 'system')
              }
            >
              <FormControlLabel value="light" control={<Radio />} label="فاتح" />
              <FormControlLabel value="dark" control={<Radio />} label="داكن" />
              <FormControlLabel value="system" control={<Radio />} label="تلقائي (حسب الجهاز)" />
            </RadioGroup>
          </FormControl>
          <Box sx={{ mt: 2 }}>
            <Button
              variant="contained"
              disabled={savingThemeDefault}
              onClick={async () => {
                try {
                  setSavingThemeDefault(true)
                  await setDoc(
                    doc(db, 'settings', 'app_appearance'),
                    {
                      defaultThemeMode,
                      updatedAt: new Date().toISOString(),
                    },
                    { merge: true },
                  )
                  setSnack({ open: true, msg: 'تم حفظ المظهر الافتراضي', sev: 'success' })
                } catch (e: any) {
                  setSnack({ open: true, msg: e.message || 'فشل الحفظ', sev: 'error' })
                } finally {
                  setSavingThemeDefault(false)
                }
              }}
            >
              {savingThemeDefault ? 'جارِ الحفظ...' : 'حفظ المظهر الافتراضي'}
            </Button>
          </Box>
        </CardContent>
      </Card>
      {/* إعدادات عدد الطلبات المسموح في نفس الوقت */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" sx={{ mb: 2 }}>عدد الطلبات المسموح في نفس الوقت</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            حدد عدد الطلبات التي يمكن للمستخدم إرسالها في نفس الوقت. إذا كان العدد = 1، يمكن للمشتري إرسال طلب واحد فقط ولا يستطيع إرسال طلب آخر إلا إذا تم إلغاء الطلب الأول أو تم حذفه بسبب انتهاء صلاحيته.
          </Typography>
          <Stack direction="row" spacing={2} sx={{ maxWidth: 400, alignItems: 'center' }}>
            <TextField 
              label="عدد الطلبات المسموح" 
              type="number" 
              value={maxConcurrentRequests} 
              onChange={e => setMaxConcurrentRequests(Number(e.target.value))}
              inputProps={{ min: 1, max: 10 }}
              sx={{ width: 200 }}
            />
            <Button variant="contained" onClick={saveMaxConcurrentRequests} disabled={savingMaxRequests}>
              {savingMaxRequests ? 'جارِ الحفظ...' : 'حفظ'}
            </Button>
          </Stack>
        </CardContent>
      </Card>

      {/* إعدادات قيود المشترين */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" sx={{ mb: 2 }}>إعدادات قيود المشترين</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            هذه الإعدادات تساعد في منع التلاعب والتأكد من جدية المشترين
          </Typography>

          <Stack spacing={3}>
            {/* حدود الإلغاءات */}
            <Box>
              <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 'bold' }}>
                حدود الإلغاءات المسموحة
              </Typography>
              <Stack spacing={2}>
                <Stack direction="row" spacing={2} alignItems="center">
                  <TextField
                    label="حد الإلغاءات (آخر 7 أيام)"
                    type="number"
                    value={buyerRestrictions.maxCancellations7Days}
                    onChange={e => setBuyerRestrictions({
                      ...buyerRestrictions,
                      maxCancellations7Days: Number(e.target.value),
                    })}
                    inputProps={{ min: 1, max: 20 }}
                    sx={{ width: 250 }}
                  />
                  <Typography variant="body2" color="text.secondary">
                    إذا تجاوز المشتري هذا الحد، سيتم تقييده من إنشاء طلبات جديدة
                  </Typography>
                </Stack>
                <Stack direction="row" spacing={2} alignItems="center">
                  <TextField
                    label="حد الإلغاءات (آخر 30 يوم)"
                    type="number"
                    value={buyerRestrictions.maxCancellations30Days}
                    onChange={e => setBuyerRestrictions({
                      ...buyerRestrictions,
                      maxCancellations30Days: Number(e.target.value),
                    })}
                    inputProps={{ min: 1, max: 50 }}
                    sx={{ width: 250 }}
                  />
                  <Typography variant="body2" color="text.secondary">
                    إذا تجاوز المشتري هذا الحد، سيتم تقييده لمدة أطول
                  </Typography>
                </Stack>
              </Stack>
            </Box>

            <Divider />

            {/* فترة التبريد بعد الإلغاء */}
            <Box>
              <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 'bold' }}>
                فترة التبريد بعد الإلغاء (بالساعات)
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                الفترة التي يجب على المشتري انتظارها قبل إنشاء طلب جديد بعد الإلغاء (حسب نسبة قبوله للعروض)
              </Typography>
              <Stack spacing={2}>
                <Stack direction="row" spacing={2} alignItems="center">
                  <TextField
                    label="إذا نسبة القبول >= 50%"
                    type="number"
                    value={buyerRestrictions.cooldownHoursExcellentAcceptance}
                    onChange={e => setBuyerRestrictions({
                      ...buyerRestrictions,
                      cooldownHoursExcellentAcceptance: Number(e.target.value),
                    })}
                    inputProps={{ min: 0, max: 168 }}
                    sx={{ width: 200 }}
                  />
                  <Typography variant="body2" color="text.secondary">
                    ساعة (سمعة ممتازة)
                  </Typography>
                </Stack>
                <Stack direction="row" spacing={2} alignItems="center">
                  <TextField
                    label="إذا نسبة القبول 30-49%"
                    type="number"
                    value={buyerRestrictions.cooldownHoursHighAcceptance}
                    onChange={e => setBuyerRestrictions({
                      ...buyerRestrictions,
                      cooldownHoursHighAcceptance: Number(e.target.value),
                    })}
                    inputProps={{ min: 0, max: 168 }}
                    sx={{ width: 200 }}
                  />
                  <Typography variant="body2" color="text.secondary">
                    ساعة
                  </Typography>
                </Stack>
                <Stack direction="row" spacing={2} alignItems="center">
                  <TextField
                    label="إذا نسبة القبول 20-29%"
                    type="number"
                    value={buyerRestrictions.cooldownHoursMediumAcceptance}
                    onChange={e => setBuyerRestrictions({
                      ...buyerRestrictions,
                      cooldownHoursMediumAcceptance: Number(e.target.value),
                    })}
                    inputProps={{ min: 0, max: 168 }}
                    sx={{ width: 200 }}
                  />
                  <Typography variant="body2" color="text.secondary">
                    ساعة
                  </Typography>
                </Stack>
                <Stack direction="row" spacing={2} alignItems="center">
                  <TextField
                    label="إذا نسبة القبول &lt; 20%"
                    type="number"
                    value={buyerRestrictions.cooldownHoursLowAcceptance}
                    onChange={e => setBuyerRestrictions({
                      ...buyerRestrictions,
                      cooldownHoursLowAcceptance: Number(e.target.value),
                    })}
                    inputProps={{ min: 0, max: 168 }}
                    sx={{ width: 200 }}
                  />
                  <Typography variant="body2" color="text.secondary">
                    ساعة
                  </Typography>
                </Stack>
              </Stack>
            </Box>

            <Divider />

            {/* حد الطلبات اليومي والأسبوعي (لجميع المستخدمين) */}
            <Box>
              <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 'bold' }}>
                حد الطلبات (اليوم والأسبوع)
              </Typography>
              <Stack spacing={2}>
                <Stack direction="row" spacing={2} alignItems="center">
                  <TextField
                    label="عدد الطلبات المسموحة في اليوم"
                    type="number"
                    value={buyerRestrictions.maxRequestsNewUserDay1}
                    onChange={e => setBuyerRestrictions({
                      ...buyerRestrictions,
                      maxRequestsNewUserDay1: Number(e.target.value),
                    })}
                    inputProps={{ min: 1, max: 10 }}
                    sx={{ width: 200 }}
                  />
                  <Typography variant="body2" color="text.secondary">
                    الحد الأقصى لعدد الطلبات في نفس اليوم (لجميع المستخدمين)
                  </Typography>
                </Stack>
                <Stack direction="row" spacing={2} alignItems="center">
                  <TextField
                    label="عدد الطلبات المسموحة في الأسبوع"
                    type="number"
                    value={buyerRestrictions.maxRequestsNewUserWeek1}
                    onChange={e => setBuyerRestrictions({
                      ...buyerRestrictions,
                      maxRequestsNewUserWeek1: Number(e.target.value),
                    })}
                    inputProps={{ min: 1, max: 50 }}
                    sx={{ width: 200 }}
                  />
                  <Typography variant="body2" color="text.secondary">
                    الحد الأقصى لعدد الطلبات في آخر 7 أيام (لجميع المستخدمين)
                  </Typography>
                </Stack>
              </Stack>
            </Box>

            <Button
              variant="contained"
              onClick={saveBuyerRestrictions}
              disabled={savingBuyerRestrictions}
              sx={{ mt: 2 }}
            >
              {savingBuyerRestrictions ? 'جارِ الحفظ...' : 'حفظ إعدادات قيود المشترين'}
            </Button>
          </Stack>
        </CardContent>
      </Card>

      {/* إعدادات نطاق البحث وطريقة البحث */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" sx={{ mb: 2 }}>طريقة البحث في التطبيق</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            اختر الطريقة التي تظهر للمشتري في التطبيق. إذا اخترت «البحث العادي» فلن يظهر خيار البحث الذكي، وإذا اخترت «البحث الذكي» فلن يظهر خيار النطاق الجغرافي.
          </Typography>
          <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
            <FormControlLabel
              control={
                <Switch
                  checked={allowedSearchMode === 'smart'}
                  onChange={(_, checked) => setAllowedSearchMode(checked ? 'smart' : 'radius')}
                  color="primary"
                />
              }
              label={allowedSearchMode === 'smart' ? 'البحث الذكي' : 'البحث العادي بالنطاق الجغرافي'}
            />
          </Stack>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            الحالي: <strong>{allowedSearchMode === 'smart' ? 'البحث الذكي' : 'البحث العادي بالنطاق الجغرافي'}</strong>
          </Typography>

          <Divider sx={{ my: 3 }} />

          <Typography variant="h6" sx={{ mb: 2 }}>إعدادات نطاق البحث (للبحث العادي)</Typography>
          <Stack direction="row" spacing={2} sx={{ maxWidth: 600 }}>
            <TextField label="الحد الأدنى (كم)" type="number" value={minRangeKm} onChange={e => setMinRangeKm(Number(e.target.value))} />
            <TextField label="الحد الأقصى (كم)" type="number" value={maxRangeKm} onChange={e => setMaxRangeKm(Number(e.target.value))} />
            <Button variant="contained" onClick={save} disabled={saving}>{saving ? 'جارِ الحفظ...' : 'حفظ'}</Button>
          </Stack>
        </CardContent>
      </Card>
      </>
      )}

      {/* تبويب إعدادات الفئات — تصميم موحّد بأقسام قابلة للطي */}
      {currentTab === 2 && (
        <>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        إعدادات مرتبطة بكل فئة: مدة صلاحية العروض، نسبة العمولة، مدة حجز المنتج، ومدة صلاحية الطلبات. اختر القسم من القائمة ثم عدّل القيم.
      </Typography>

      <Accordion defaultExpanded={false} sx={{ mb: 1, '&:before': { display: 'none' }, boxShadow: 1 }}>
        <AccordionSummary expandIcon={<ExpandMore />} sx={{ bgcolor: 'grey.50', borderRadius: 1 }}>
          <Typography variant="subtitle1" fontWeight="bold">إعدادات مدة صلاحية العروض</Typography>
        </AccordionSummary>
        <AccordionDetails sx={{ pt: 2 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            حدد مدة صلاحية العروض لكل فئة (بالدقائق)
          </Typography>
          <Box sx={{ maxWidth: 520, mb: 2 }}>
            {availableCategories.map(cat => (
              <Stack key={cat.id} direction="row" alignItems="center" spacing={2} sx={{ mb: 1.5 }}>
                <Typography variant="body2" sx={{ width: 280, flexShrink: 0, overflow: 'hidden', textOverflow: 'ellipsis' }}>{cat.name}</Typography>
                <TextField
                  type="number"
                  size="small"
                  value={categoryExpiry[cat.id] ?? 5}
                  onChange={e => updateCategoryExpiry(cat.id, Number(e.target.value))}
                  onFocus={() => addNewCategory(cat.id)}
                  inputProps={{ min: 1, max: 1440 }}
                  sx={{ width: 90, flexShrink: 0 }}
                  variant="outlined"
                />
                <Typography variant="body2" color="text.secondary">دقيقة</Typography>
              </Stack>
            ))}
          </Box>
          <Button variant="contained" onClick={saveExpirySettings} disabled={savingExpiry || availableCategories.length === 0} size="medium">
            {savingExpiry ? 'جارِ الحفظ...' : 'حفظ مدة صلاحية العروض'}
          </Button>
        </AccordionDetails>
      </Accordion>

      <Accordion defaultExpanded={false} sx={{ mb: 1, '&:before': { display: 'none' }, boxShadow: 1 }}>
        <AccordionSummary expandIcon={<ExpandMore />} sx={{ bgcolor: 'grey.50', borderRadius: 1 }}>
          <Typography variant="subtitle1" fontWeight="bold">إعدادات نسبة العمولة</Typography>
        </AccordionSummary>
        <AccordionDetails sx={{ pt: 2 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            نسبة العمولة كنسبة مئوية من السعر. الافتراضي 0%.
          </Typography>
          <Alert severity="info" sx={{ mb: 2, borderRadius: 2 }}>مثال: نسبة 2% وسعر 1000 جنيه → الصافي 980 جنيه</Alert>
          <Box sx={{ maxWidth: 520 }}>
            {availableCategories.map(cat => (
              <Stack key={cat.id} direction="row" alignItems="center" spacing={2} sx={{ mb: 1.5 }}>
                <Typography variant="body2" sx={{ width: 280, flexShrink: 0, overflow: 'hidden', textOverflow: 'ellipsis' }}>{cat.name}</Typography>
                <TextField
                  type="number"
                  size="small"
                  value={commissionRates[cat.id] ?? 0}
                  onChange={e => updateCommissionRate(cat.id, Number(e.target.value))}
                  onFocus={() => addNewCommissionCategory(cat.id)}
                  inputProps={{ min: 0, max: 100, step: 0.1 }}
                  sx={{ width: 90, flexShrink: 0 }}
                  variant="outlined"
                />
                <Typography variant="body2" color="text.secondary">%</Typography>
              </Stack>
            ))}
          </Box>
          <Stack direction="row" spacing={2} sx={{ mt: 2 }}>
            <Button variant="outlined" onClick={resetCommissionToDefaults} disabled={savingCommission} color="secondary" size="medium">إعادة تعيين</Button>
            <Button variant="contained" onClick={saveCommissionSettings} disabled={savingCommission} size="medium">
              {savingCommission ? 'جارِ الحفظ...' : 'حفظ نسبة العمولة'}
            </Button>
          </Stack>
        </AccordionDetails>
      </Accordion>

      <Accordion defaultExpanded={false} sx={{ mb: 1, '&:before': { display: 'none' }, boxShadow: 1 }}>
        <AccordionSummary expandIcon={<ExpandMore />} sx={{ bgcolor: 'grey.50', borderRadius: 1 }}>
          <Typography variant="subtitle1" fontWeight="bold">إعدادات مدة حجز المنتج</Typography>
        </AccordionSummary>
        <AccordionDetails sx={{ pt: 2 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            مدة حجز المنتج بعد تأكيد الدفع (بالساعات). بعد انتهائها يعتبر الطلب لاغياً.
          </Typography>
          <Box sx={{ maxWidth: 520, mb: 2 }}>
            {availableCategories.map(cat => (
              <Stack key={cat.id} direction="row" alignItems="center" spacing={2} sx={{ mb: 1.5 }}>
                <Typography variant="body2" sx={{ width: 280, flexShrink: 0, overflow: 'hidden', textOverflow: 'ellipsis' }}>{cat.name}</Typography>
                <TextField
                  type="number"
                  size="small"
                  value={reservationDuration[cat.id] ?? 1}
                  onChange={e => updateReservationDuration(cat.id, Number(e.target.value))}
                  onFocus={() => addNewReservationCategory(cat.id)}
                  inputProps={{ min: 1, max: 168 }}
                  sx={{ width: 90, flexShrink: 0 }}
                  variant="outlined"
                />
                <Typography variant="body2" color="text.secondary">ساعة</Typography>
              </Stack>
            ))}
          </Box>
          <Button variant="contained" onClick={saveReservationDurationSettings} disabled={savingReservationDuration || availableCategories.length === 0} size="medium">
            {savingReservationDuration ? 'جارِ الحفظ...' : 'حفظ مدة الحجز'}
          </Button>
        </AccordionDetails>
      </Accordion>

      <Accordion defaultExpanded={false} sx={{ mb: 1, '&:before': { display: 'none' }, boxShadow: 1 }}>
        <AccordionSummary expandIcon={<ExpandMore />} sx={{ bgcolor: 'grey.50', borderRadius: 1 }}>
          <Typography variant="subtitle1" fontWeight="bold">إعدادات مدة صلاحية الطلبات</Typography>
        </AccordionSummary>
        <AccordionDetails sx={{ pt: 2 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            مدة صلاحية طلب المشتري (طلب عروض أسعار) لكل فئة (بالساعات). بعد انتهائها يُحذف الطلب إن لم يُقبل عرض.
          </Typography>
          <Box sx={{ maxWidth: 520, mb: 2 }}>
            {availableCategories.map(cat => (
              <Stack key={cat.id} direction="row" alignItems="center" spacing={2} sx={{ mb: 1.5 }}>
                <Typography variant="body2" sx={{ width: 280, flexShrink: 0, overflow: 'hidden', textOverflow: 'ellipsis' }}>{cat.name}</Typography>
                <TextField
                  type="number"
                  size="small"
                  value={requestExpiry[cat.id] ?? 24}
                  onChange={e => updateRequestExpiry(cat.id, Number(e.target.value))}
                  onFocus={() => addNewRequestExpiryCategory(cat.id)}
                  inputProps={{ min: 1, max: 168 }}
                  sx={{ width: 90, flexShrink: 0 }}
                  variant="outlined"
                />
                <Typography variant="body2" color="text.secondary">ساعة</Typography>
              </Stack>
            ))}
          </Box>
          <Button variant="contained" onClick={saveRequestExpirySettings} disabled={savingRequestExpiry || availableCategories.length === 0} size="medium">
            {savingRequestExpiry ? 'جارِ الحفظ...' : 'حفظ مدة صلاحية الطلبات'}
          </Button>
        </AccordionDetails>
      </Accordion>
      </>
      )}

      {/* تبويب البحث الذكي (محلي فقط) */}
      {currentTab === 5 && (
        <>
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" sx={{ mb: 2, color: '#FF6600', fontWeight: 'bold' }}>
              إعدادات البحث الذكي (Smart Search)
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              تتحكم هذه الإعدادات في سلوك البحث الذكي عند إنشاء الطلب (استخدام البحث الذكي بدلاً من نطاق الخريطة).
              التعديلات تُطبَّق على البيئة المرتبطة بلوحة التحكم (محلياً). لن تُنشر للإنتاج إلا عند النشر يدوياً.
            </Typography>
            <Stack spacing={2} sx={{ maxWidth: 500 }}>
              <TextField
                label="فاصل الموجة (دقائق)"
                type="number"
                value={smartSearch.wave_interval_minutes}
                onChange={e => setSmartSearch(prev => ({ ...prev, wave_interval_minutes: Number(e.target.value) }))}
                inputProps={{ min: 1, max: 60 }}
                helperText="المدة بين كل موجة وتالية (دقائق)"
              />
              <TextField
                label="عدد التجار في الموجة الأولى"
                type="number"
                value={smartSearch.initial_merchants_count}
                onChange={e => setSmartSearch(prev => ({ ...prev, initial_merchants_count: Number(e.target.value) }))}
                inputProps={{ min: 1, max: 50 }}
                helperText="أقرب هذا العدد من التجار داخل نطاق البحث (نفس الفئة، حسب القرب ثم التقييم)"
              />
              <TextField
                label="عدد التجار في كل موجة تالية"
                type="number"
                value={smartSearch.wave_expansion_count}
                onChange={e => setSmartSearch(prev => ({ ...prev, wave_expansion_count: Number(e.target.value) }))}
                inputProps={{ min: 1, max: 20 }}
                helperText="كم تاجر تُرسل لهم كل موجة تالية (الأقرب من غير المُشعَرين)"
              />
              <TextField
                label="الحد الأقصى لعدد الموجات"
                type="number"
                value={smartSearch.max_waves}
                onChange={e => setSmartSearch(prev => ({ ...prev, max_waves: Number(e.target.value) }))}
                inputProps={{ min: 1, max: 20 }}
                helperText="بعد هذا العدد من الموجات تتوقف عملية التوسع حتى لو لم يُستلم العدد المطلوب من العروض"
              />
              <TextField
                label="الحد الأقصى لعدد العروض"
                type="number"
                value={smartSearch.max_offers_limit}
                onChange={e => setSmartSearch(prev => ({ ...prev, max_offers_limit: Number(e.target.value) }))}
                inputProps={{ min: 1, max: 50 }}
                helperText="عند الوصول لهذا العدد يتوقف قبول عروض جديدة"
              />
              <TextField
                label="عدد العروض الكافي لإيقاف التوسع"
                type="number"
                value={smartSearch.min_offers_to_stop_expansion}
                onChange={e => setSmartSearch(prev => ({ ...prev, min_offers_to_stop_expansion: Number(e.target.value) }))}
                inputProps={{ min: 0, max: 30 }}
                helperText="إذا وصل عدد العروض لهذا الحد تتوقف الموجات الجديدة (قبل الوصول للحد الأقصى للموجات)"
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={smartSearch.enforce_governorate_boundary}
                    onChange={(_, v) => setSmartSearch(prev => ({ ...prev, enforce_governorate_boundary: v }))}
                    color="warning"
                  />
                }
                label={
                  <Box>
                    <Typography variant="body2" fontWeight={600}>
                      تقييد البحث الذكي بنطاق محافظة المشتري
                    </Typography>
                    <Typography variant="caption" color="text.secondary" display="block">
                      عند التفعيل: لا تتجاوز الموجات محافظة الطلب؛ القاهرة والجيزة والقليوبية تُعامل كمنطقة واحدة (القاهرة الكبرى).
                      عند الإيقاف: السلوك السابق (توسع حسب القرب فقط عبر مصر).
                    </Typography>
                  </Box>
                }
              />
            </Stack>
            <Button
              variant="contained"
              onClick={async () => {
                try {
                  setSavingSmartSearch(true)
                  await setDoc(doc(db, 'settings', 'smart_search'), {
                    ...smartSearch,
                    updatedAt: new Date().toISOString(),
                  }, { merge: true })
                  setSnack({ open: true, msg: 'تم حفظ إعدادات البحث الذكي بنجاح', sev: 'success' })
                } catch (e: any) {
                  setSnack({ open: true, msg: e.message || 'فشل الحفظ', sev: 'error' })
                } finally {
                  setSavingSmartSearch(false)
                }
              }}
              disabled={savingSmartSearch}
              sx={{ mt: 3 }}
            >
              {savingSmartSearch ? 'جارِ الحفظ...' : 'حفظ إعدادات البحث الذكي'}
            </Button>
          </CardContent>
        </Card>

        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" sx={{ mb: 2, color: '#FF6600', fontWeight: 'bold' }}>
              تذكير التجار بالطلبات الجديدة
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              إذا لم يفتح التاجر الطلب بعد الإشعار الأول، يُرسل تذكير تلقائي من السيرفر. يتوقف التذكير
              فور فتح الطلب أو عند انتهاء/قبول/إلغاء الطلب. لا يحتاج تحديث التطبيق لتغيير التوقيتات.
            </Typography>
            <Stack spacing={2} sx={{ maxWidth: 560 }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={requestReminders.enabled}
                    onChange={(_, v) => setRequestReminders(prev => ({ ...prev, enabled: v }))}
                  />
                }
                label="تفعيل التذكيرات"
              />
              <TextField
                label="التأخير قبل التذكير الأول (ثوانٍ)"
                type="number"
                value={requestReminders.first_delay_seconds}
                onChange={e =>
                  setRequestReminders(prev => ({ ...prev, first_delay_seconds: Number(e.target.value) }))
                }
                inputProps={{ min: 15, max: 600 }}
                helperText="الافتراضي: 60 ثانية (دقيقة واحدة)"
              />
              <TextField
                label="عدد التذكيرات الأقصى لكل تاجر"
                type="number"
                value={requestReminders.max_reminders_per_seller}
                onChange={e =>
                  setRequestReminders(prev => ({
                    ...prev,
                    max_reminders_per_seller: Number(e.target.value),
                  }))
                }
                inputProps={{ min: 0, max: 3 }}
                helperText="0 = إيقاف التذكير (مع بقاء الإشعار الأول). 1 موصى به للبداية."
              />
              <TextField
                label="التأخير قبل التذكير الثاني (ثوانٍ)"
                type="number"
                value={requestReminders.second_delay_seconds}
                onChange={e =>
                  setRequestReminders(prev => ({ ...prev, second_delay_seconds: Number(e.target.value) }))
                }
                inputProps={{ min: 30, max: 3600 }}
                helperText="يُستخدم فقط إذا كان عدد التذكيرات ≥ 2"
                disabled={requestReminders.max_reminders_per_seller < 2}
              />
              <TextField
                label="عنوان التذكير (عربي)"
                value={requestReminders.reminder_title_ar}
                onChange={e =>
                  setRequestReminders(prev => ({ ...prev, reminder_title_ar: e.target.value }))
                }
                fullWidth
              />
              <TextField
                label="نص التذكير (عربي)"
                value={requestReminders.reminder_body_ar}
                onChange={e =>
                  setRequestReminders(prev => ({ ...prev, reminder_body_ar: e.target.value }))
                }
                helperText="استخدم {productName} كمتغير لاسم المنتج"
                fullWidth
                multiline
                minRows={2}
              />
            </Stack>
            <Button
              variant="contained"
              onClick={async () => {
                try {
                  setSavingRequestReminders(true)
                  await setDoc(
                    doc(db, 'settings', 'request_reminders'),
                    {
                      ...requestReminders,
                      updatedAt: new Date().toISOString(),
                    },
                    { merge: true },
                  )
                  setSnack({
                    open: true,
                    msg: 'تم حفظ إعدادات تذكير التجار بنجاح',
                    sev: 'success',
                  })
                } catch (e: any) {
                  setSnack({ open: true, msg: e.message || 'فشل الحفظ', sev: 'error' })
                } finally {
                  setSavingRequestReminders(false)
                }
              }}
              disabled={savingRequestReminders}
              sx={{ mt: 3 }}
            >
              {savingRequestReminders ? 'جارِ الحفظ...' : 'حفظ إعدادات التذكير'}
            </Button>
          </CardContent>
        </Card>
        </>
      )}

      {/* تبويب صور الـ Slider */}
      {currentTab === 4 && (
      <>
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" sx={{ mb: 2, color: '#FF6600', fontWeight: 'bold' }}>
            إدارة صور الـ Slider الرئيسي
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            يمكنك إضافة وحذف وترتيب الصور التي تظهر في الـ Slider الرئيسي في الشاشة الرئيسية للمشتري
          </Typography>

          {/* زر رفع صورة */}
          <input
            type="file"
            accept="image/*"
            onChange={(e) => {
              const file = e.target.files?.[0]
              if (file) handleUploadSliderImage(file)
              e.target.value = '' // إعادة تعيين input
            }}
            style={{ display: 'none' }}
            id="slider-image-upload"
          />
          <label htmlFor="slider-image-upload">
            <Button
              variant="contained"
              component="span"
              startIcon={<CloudUpload />}
              disabled={uploadingSliderImage}
              sx={{ mb: 3 }}
            >
              {uploadingSliderImage ? 'جاري الرفع...' : 'إضافة صورة جديدة'}
            </Button>
          </label>

          {/* قائمة الصور */}
          {sliderImages.length === 0 ? (
            <Alert severity="info" sx={{ mt: 2 }}>
              لا توجد صور حالياً. قم بإضافة صور للعرض في الـ Slider.
            </Alert>
          ) : (
            <Stack spacing={2} sx={{ mt: 2 }}>
              {sliderImages.map((image, index) => (
                <Card key={image.id} variant="outlined">
                  <CardContent>
                    <Grid container spacing={2} alignItems="center">
                      <Grid item xs={12} sm={4}>
                        <img 
                          src={image.imageUrl} 
                          alt={`Slider ${index + 1}`}
                          style={{ 
                            width: '100%', 
                            maxHeight: 150, 
                            objectFit: 'cover',
                            borderRadius: 8,
                            border: '1px solid #e0e0e0'
                          }}
                        />
                      </Grid>
                      <Grid item xs={12} sm={5}>
                        <Typography variant="body2" color="text.secondary">
                          الترتيب: {image.order}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1, wordBreak: 'break-all' }}>
                          {image.imageUrl}
                        </Typography>
                        <FormControlLabel
                          control={
                            <Switch
                              checked={image.isActive}
                              onChange={(e) => handleToggleSliderImageActive(image.id, e.target.checked)}
                              disabled={updatingSliderImage === image.id}
                              color="primary"
                            />
                          }
                          label={
                            <Typography variant="body2" color={image.isActive ? 'success.main' : 'text.secondary'}>
                              {image.isActive ? 'نشط' : 'معطل'}
                            </Typography>
                          }
                          sx={{ mt: 1 }}
                        />
                      </Grid>
                      <Grid item xs={12} sm={3}>
                        <Stack direction="row" spacing={1} justifyContent="flex-end">
                          <IconButton
                            size="small"
                            onClick={() => handleMoveSliderImage(image.id, 'up')}
                            disabled={index === 0}
                            color="primary"
                          >
                            <ArrowUpward />
                          </IconButton>
                          <IconButton
                            size="small"
                            onClick={() => handleMoveSliderImage(image.id, 'down')}
                            disabled={index === sliderImages.length - 1}
                            color="primary"
                          >
                            <ArrowDownward />
                          </IconButton>
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => handleDeleteSliderImage(image.id)}
                          >
                            <Delete />
                          </IconButton>
                        </Stack>
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>
              ))}
            </Stack>
          )}
        </CardContent>
      </Card>
      </>
      )}

      {/* تبويب طرق الدفع */}
      {currentTab === 3 && (
      <>
      {/* إعدادات طرق الدفع */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" sx={{ mb: 2, color: '#FF6600', fontWeight: 'bold' }}>
            إعدادات طرق الدفع
          </Typography>
          
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            قم بتفعيل أو تعطيل طرق الدفع المتاحة في التطبيق. الطرق المعطلة ستظهر للمستخدمين ولكن بشكل غير نشط.
          </Typography>

          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={4}>
              <FormControlLabel
                control={
                  <Switch
                    checked={paymentMethods.creditCard}
                    onChange={e => updatePaymentMethod('creditCard', e.target.checked)}
                    color="primary"
                  />
                }
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <span>💳</span>
                    <Typography sx={{ opacity: paymentMethods.creditCard ? 1 : 0.6 }}>
                      بطاقة ائتمانية
                    </Typography>
                  </Box>
                }
              />
            </Grid>

            <Grid item xs={12} sm={6} md={4}>
              <FormControlLabel
                control={
                  <Switch
                    checked={paymentMethods.electronicWallet}
                    onChange={e => updatePaymentMethod('electronicWallet', e.target.checked)}
                    color="primary"
                  />
                }
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <span>💰</span>
                    <Typography sx={{ opacity: paymentMethods.electronicWallet ? 1 : 0.6 }}>
                      محفظة إلكترونية
                    </Typography>
                  </Box>
                }
              />
            </Grid>

            <Grid item xs={12} sm={6} md={4}>
              <FormControlLabel
                control={
                  <Switch
                    checked={paymentMethods.appWallet}
                    onChange={e => updatePaymentMethod('appWallet', e.target.checked)}
                    color="primary"
                  />
                }
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <span>📱</span>
                    <Typography sx={{ opacity: paymentMethods.appWallet ? 1 : 0.6 }}>
                      محفظة التطبيق
                    </Typography>
                  </Box>
                }
              />
            </Grid>

            <Grid item xs={12} sm={6} md={4}>
              <FormControlLabel
                control={
                  <Switch
                    checked={paymentMethods.bankTransfer}
                    onChange={e => updatePaymentMethod('bankTransfer', e.target.checked)}
                    color="primary"
                  />
                }
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <span>🏦</span>
                    <Typography sx={{ opacity: paymentMethods.bankTransfer ? 1 : 0.6 }}>
                      تحويل بنكي
                    </Typography>
                  </Box>
                }
              />
            </Grid>

            <Grid item xs={12} sm={6} md={4}>
              <FormControlLabel
                control={
                  <Switch
                    checked={paymentMethods.testPayment}
                    onChange={e => updatePaymentMethod('testPayment', e.target.checked)}
                    color="primary"
                  />
                }
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <span>🔧</span>
                    <Typography sx={{ opacity: paymentMethods.testPayment ? 1 : 0.6 }}>
                      دفع تجريبي
                    </Typography>
                  </Box>
                }
              />
            </Grid>

            <Grid item xs={12} sm={6} md={4}>
              <FormControlLabel
                control={
                  <Switch
                    checked={paymentMethods.cashOnDelivery}
                    onChange={e => updatePaymentMethod('cashOnDelivery', e.target.checked)}
                    color="primary"
                  />
                }
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <span>💵</span>
                    <Typography sx={{ opacity: paymentMethods.cashOnDelivery ? 1 : 0.6 }}>
                      نقداً عند الاستلام
                    </Typography>
                  </Box>
                }
              />
            </Grid>
          </Grid>

          <Alert severity="info" sx={{ mt: 2 }}>
            <Typography variant="body2">
              <strong>ملاحظة:</strong> طريقة "نقداً عند الاستلام" معطلة افتراضياً لأن نظام العمولة يتطلب دفع مقدم. 
              يمكن تفعيلها إذا تم تعديل نظام الدفع مستقبلاً.
            </Typography>
          </Alert>

          <Stack direction="row" spacing={2} sx={{ mt: 2 }}>
            <Button 
              variant="contained" 
              onClick={savePaymentMethodsSettings} 
              disabled={savingPaymentMethods}
            >
              {savingPaymentMethods ? 'جارِ الحفظ...' : 'حفظ إعدادات طرق الدفع'}
            </Button>
          </Stack>
        </CardContent>
      </Card>
      </>
      )}

      {currentTab === 6 && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" sx={{ mb: 2 }}>
              شروط الطلب الاختيارية (تطبيق المشتري)
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              تحكم في إظهار خيارات: دفع إلكتروني، تقسيط، توصيل، استلام من المتجر. الطلب لا يصل إلا للتجار الذين يوفّرون الخدمات المحددة.
            </Typography>
            <Stack spacing={1}>
              <FormControlLabel
                control={
                  <Switch
                    checked={orderConditionsSettings.enabled}
                    onChange={(e) =>
                      setOrderConditionsSettings((s) => ({ ...s, enabled: e.target.checked }))
                    }
                  />
                }
                label="تفعيل قسم شروط الطلب في التطبيق"
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={orderConditionsSettings.showElectronicPayment}
                    onChange={(e) =>
                      setOrderConditionsSettings((s) => ({
                        ...s,
                        showElectronicPayment: e.target.checked,
                      }))
                    }
                  />
                }
                label="إظهار: دفع إلكتروني"
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={orderConditionsSettings.showInstallment}
                    onChange={(e) =>
                      setOrderConditionsSettings((s) => ({
                        ...s,
                        showInstallment: e.target.checked,
                      }))
                    }
                  />
                }
                label="إظهار: تقسيط"
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={orderConditionsSettings.showDelivery}
                    onChange={(e) =>
                      setOrderConditionsSettings((s) => ({ ...s, showDelivery: e.target.checked }))
                    }
                  />
                }
                label="إظهار: توصيل"
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={orderConditionsSettings.showStorePickup}
                    onChange={(e) =>
                      setOrderConditionsSettings((s) => ({
                        ...s,
                        showStorePickup: e.target.checked,
                      }))
                    }
                  />
                }
                label="إظهار: استلام من المتجر"
              />
            </Stack>
            <Button
              variant="contained"
              sx={{ mt: 2 }}
              onClick={() => void saveOrderConditionsSettings()}
              disabled={savingOrderConditions}
            >
              {savingOrderConditions ? 'جارِ الحفظ...' : 'حفظ'}
            </Button>
          </CardContent>
        </Card>
      )}

      <Snackbar open={snack.open} autoHideDuration={2500} onClose={() => setSnack({...snack, open:false})} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert onClose={() => setSnack({...snack, open:false})} severity={snack.sev} sx={{ width: '100%' }}>
          {snack.msg}
        </Alert>
      </Snackbar>
    </Box>
  )
}
